import React from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage, injectIntl} from "react-intl";
import {useFormikContext} from "formik";

import Label from "components/input/new/Label";
import InputTextField from "components/input/new/InputTextField";
import {getTranslation} from "../../../../../../../../../domain/helpers/translations";
import StyledReactSelect from "../../../../../../../../../components/select/StyledReactSelect";
import FormControlLabel from "../../../../../../../../../components/radioGroup/FormControlLabel";
import RadioGroup from "../../../../../../../../../components/radioGroup/RadioGroup";
import {LocalizationProvider, StaticDateTimePicker} from "@mui/x-date-pickers";
import DateTimePicker from "components/date/DateTimePicker";
import {AdapterMoment} from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import CustomSwitch from "../../../../../../../../../components/switch/CustomSwitch";

AttributeField.propTypes = {
    attribute: PropTypes.object,
};

function AttributeField({name, attribute, ...props}) {
    const {values, handleChange, handleBlur, touched, errors, setFieldValue} = useFormikContext()

    function getAttributeChoices(attributeValue) {
        const collection = attribute.choices.filter(item => item['@id'] === attributeValue);
        if (collection.length) {
            return {
                value: collection[0]['@id'],
                label: collection[0].label
            };
        }

        return null;
    }

    let currentAttributeChoice = null;
    if (values.attributes[attribute.id] && values.attributes[attribute.id].attributeChoices) {
        currentAttributeChoice = values.attributes[attribute.id].attributeChoices[0];
    }

    return (
        <>
            { attribute.type !== 'bool' &&
                <Label required={!!attribute.required} htmlFor={getTranslation(attribute).name}>
                    {getTranslation(attribute).name}
                </Label>
            }
            {(attribute.type === 'string' || attribute.type === 'int' || attribute.type === 'float' || attribute.type === 'url') ?
                <InputTextField
                    name={`attributes.${attribute.id}`}
                    onChange={(e) => {
                        let value = e.target.value;
                        if (attribute.type === 'float') {
                            value = value.replace(',', '.');
                        }
                        handleChange(e);
                        setFieldValue(`attributes.${attribute.id}`, value);
                    }}
                    value={values.attributes[attribute.id] || ''}
                    placeholder={
                        props.intl.formatMessage({id: attribute.type === 'url' ? 'product.show.product.form.field.url.placeholder' : 'product.show.product.form.field.name.placeholder'})
                    }
                    fullWidth
                    onBlur={handleBlur}
                    error={touched.attributes?.[attribute.id] && Boolean(errors.attributes?.[attribute.id])}
                    helperText={touched.attributes?.[attribute.id] && errors.attributes?.[attribute.id] ? errors.attributes[attribute.id] : ''}
                />
            : (attribute.type === 'multiple_choice' && attribute.choices.length) ?
                    <StyledReactSelect
                        isMulti
                        placeholder={<FormattedMessage id='product.show.product.form.field.attribute.multiselect.placeholder' />}
                        name={`attributes.${attribute.id}`}
                        value={values.attributes[attribute.id]?.attributeChoices?.map(item => getAttributeChoices(item)) || []}
                        options={attribute.choices.filter(choice => !values.attributes[attribute.id]?.attributeChoices?.some(selected => selected.value === choice['@id'])).map(choice => ({ value: choice['@id'], label: choice.label }))}
                        onChange={(selectedList) => {
                            setFieldValue(`attributes.${attribute.id}`, selectedList ? { attributeChoices: selectedList.map(item => item.value)} : null);
                        }}
                        onBlur={handleBlur}
                        error={touched.attributes?.[attribute.id] && Boolean(errors.attributes?.[attribute.id])}
                        helperText={touched.attributes?.[attribute.id] && errors.attributes?.[attribute.id] ? errors.attributes[attribute.id] : ''}
                    />
            : (attribute.type === 'choice' && attribute.choices.length) ?
                     <RadioGroup
                         alignListWithButton
                         selectedValueLabel={getAttributeChoices(currentAttributeChoice)?.label || null}
                         name={`attributes.${attribute.id}`}
                         value={getAttributeChoices(currentAttributeChoice)?.value || null}
                         placeholder={(<FormattedMessage id='product.show.product.form.field.attribute.select.placeholder' />)}
                         onChange={(event) => {
                             setFieldValue(`attributes.${attribute.id}`, {attributeChoices: [event.currentTarget.value]});
                         }}
                     >
                         {attribute.choices.map((item, index) => (
                             <FormControlLabel
                                 key={index}
                                 value={item['@id']}
                                 label={item.label}
                             />
                         ))}
                     </RadioGroup>
                : attribute.type === 'date' ?
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                        <DateTimePicker
                            name={`attributes.${attribute.id}`}
                            value={moment.unix(Number(values.attributes[attribute.id])).isValid() ? moment.unix(Number(values.attributes[attribute.id])) : null}
                            onChange={(date) => {
                                if (date.isValid()) {
                                    // Convert date to timestamp in seconds
                                    date = Math.floor(Date.parse(date) / 1000).toString()
                                    setFieldValue(`attributes.${attribute.id}`, date);
                                }
                            }}
                            views={['day', 'month', 'year', 'hours', 'minutes']}
                            format={'DD/MM/YYYY HH:mm'}
                        />
                    </LocalizationProvider>
                : attribute.type === 'bool' ?
                    <>
                        <CustomSwitch
                            onClick={handleChange}
                            name={`attributes.${attribute.id}`}
                            checked={undefined !== values.attributes[attribute.id] ? values.attributes[attribute.id] : false}
                            onChange={(e) => {
                                setFieldValue(`attributes.${attribute.id}`, e.target.checked);
                            }}
                        />
                        <Label required={!!attribute.required} htmlFor={getTranslation(attribute).name}>
                            {getTranslation(attribute).name}
                        </Label>
                    </>
            : null
            }
        </>
    );
}

export default injectIntl(AttributeField);
